import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["lenderFilter", "timeWindowFilter", "statusGrid"];

  // Corresponds to the STATUS_COLOR_MAP previously in VrMonitoringDataFetcher
  // We'll use this to color the squares on the frontend.
  STATUS_COLOR_MAP = {
    // Success states
    ready: { bg: "bg-green", text: "text-white" }, // Your custom green

    // Error states
    error: { bg: "bg-red", text: "text-white" }, // Your custom red
    error_avm: { bg: "#B00020", text: "text-white" }, // Darker Red (Hex)
    error_lcp: { bg: "bg-pink", text: "text-white" }, // Your custom pink
    error_home_value: { bg: "#D32F2F", text: "text-white" }, // Another distinct Red (Hex)

    // Warning/Missing/Failed states - cycling for variety
    missing_loan_amount: { bg: "bg-orange", text: "text-gray-800" }, // Your custom orange
    missing_loan_term: { bg: "bg-darkOrange", text: "text-white" }, // Your custom darkOrange
    missing_quote_rate: { bg: "bg-gold", text: "text-gray-800" }, // Your custom gold
    missing_ltv: { bg: "#FFA726", text: "text-gray-800" }, // Hex - Bright Orange
    missing_purchase_price: { bg: "#FFB74D", text: "text-gray-800" }, // Hex - Lighter Orange
    rltv_out_of_range: { bg: "#FFC107", text: "text-gray-800" }, // Hex - Amber
    missing_property_value: { bg: "#FFCA28", text: "text-gray-800" }, // Hex - Light Amber
    missing_data_enrichment: { bg: "#FFD54F", text: "text-gray-800" }, // Hex - Very Light Amber
    missing_cherre_lcp: { bg: "#FFEE58", text: "text-gray-800" }, // Hex - Bright Yellow
    failed_homeiq: { bg: "#FDD835", text: "text-gray-800" }, // Hex - Yellow/Gold
    failed_cherre: { bg: "#FBC02D", text: "text-gray-800" }, // Hex - Darker Yellow/Gold
    failed_pmms: { bg: "#FB8C00", text: "text-white" }, // Hex - Deep Orange
    missing_pmms_values_avm: { bg: "#F57C00", text: "text-white" }, // Corrected key from LenderClient enum

    error_property_address: { bg: "bg-yellow-400", text: "text-gray-800" }, // Tailwind default
    invalid_property_address: { bg: "bg-yellow-500", text: "text-gray-800" }, // Tailwind default

    // In-progress/pending states
    pending: { bg: "bg-blue-400", text: "text-white" }, // Tailwind default
    queued: { bg: "bg-blue-300", text: "text-gray-800" }, // Tailwind default (needs dark text)
    processing: { bg: "bg-blue-500", text: "text-white" }, // Tailwind default
    vro_pending: { bg: "bg-purple-400", text: "text-white" }, // Tailwind default

    // Other/neutral states
    not_ready: { bg: "bg-gray", text: "text-white" }, // Your custom gray #6E6E6E
    cancelled: { bg: "bg-grayaltsecond", text: "text-white" }, // Your custom gray #878787
    archived: { bg: "bg-grayalt", text: "text-gray-800" }, // Your custom gray #D9D9D9 (light)
    default: { bg: "bg-graylight", text: "text-gray-800" }, // Your custom gray #F2F6F7 (very light)
  };

  connect() {
    console.log("VR Monitoring Dashboard Stimulus controller connected.");
    this.populateLenderFilter();
    this.fetchAndDisplayStatusData(); // Initial fetch

    // Add event listeners to filters to re-fetch data on change
    this.lenderFilterTarget.addEventListener("change", async () => {
      await this.fetchAndDisplayStatusData();
    });
    this.timeWindowFilterTarget.addEventListener("change", async () => {
      await this.fetchAndDisplayStatusData();
    });
  }

  async populateLenderFilter() {
    try {
      const response = await fetch("/vr_monitoring/lenders");
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const lenders = await response.json();
      // Clear existing options except for "All Lenders"
      while (this.lenderFilterTarget.options.length > 1) {
        this.lenderFilterTarget.remove(1);
      }
      lenders.forEach((lender) => {
        const option = document.createElement("option");
        option.value = lender.id;
        option.textContent = lender.name;
        this.lenderFilterTarget.appendChild(option);
      });
    } catch (error) {
      console.error("Error fetching lenders:", error);
      // Optionally, display an error message to the user in the UI
    }
  }

  async fetchAndDisplayStatusData() {
    const lenderId = this.lenderFilterTarget.value;
    const timeWindowMinutes = this.timeWindowFilterTarget.value;

    // Calculate start and end times based on the selected window
    const endTime = new Date();
    const startTime = new Date(endTime.getTime() - timeWindowMinutes * 60000);

    // Format dates as ISO8601 strings for the API
    const startTimeISO = startTime.toISOString();
    const endTimeISO = endTime.toISOString();

    let url = `/vr_monitoring/status_data?start_time=${startTimeISO}&end_time=${endTimeISO}`;
    if (lenderId) {
      url += `&lender_id=${lenderId}`;
    }

    console.log(`Fetching data from: ${url}`); // For debugging
    this.statusGridTarget.innerHTML =
      '<p class="text-gray-500">Loading VR statuses...</p>'; // Show loading message

    try {
      const response = await fetch(url);
      if (!response.ok) {
        // Attempt to parse error response, provide fallback if parsing fails
        let errorDetails = { error: "Unknown error during fetch" };
        try {
          errorDetails = await response.json();
        } catch (parseError) {
          console.error(
            "Failed to parse error JSON from response:",
            parseError
          );
          errorDetails.error = response.statusText || errorDetails.error;
        }
        console.error("Error fetching status data (details):", errorDetails);
        this.statusGridTarget.innerHTML = `<p class="text-red-500">Error loading data: ${errorDetails.error}</p>`;
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      this.renderStatusGrid(data);
    } catch (error) {
      console.error("Error fetching status data (overall catch):", error);
      // Avoid overwriting specific error message if it was already set from response.json()
      if (!this.statusGridTarget.innerHTML.includes("Error loading data")) {
        this.statusGridTarget.innerHTML =
          '<p class="text-red-500">Could not fetch VR status data. Please try again.</p>';
      }
    }
  }

  renderStatusGrid(data) {
    console.log(
      "Attempting to render grid with data:",
      JSON.parse(JSON.stringify(data))
    ); // Log a copy of the data
    this.statusGridTarget.innerHTML = ""; // Clear previous content or loading message

    if (!data || data.length === 0) {
      this.statusGridTarget.innerHTML =
        '<p class="text-gray-500">No Value Reports found for the selected criteria.</p>';
      return;
    }

    try {
      data.forEach((vr) => {
        const square = document.createElement("div");
        console.log(
          `Processing VR ID: ${vr.value_report_id}, API report_status: '${vr.report_status}'`
        ); // Added log
        const statusStyle =
          this.STATUS_COLOR_MAP[vr.report_status] ||
          this.STATUS_COLOR_MAP.default;

        // Base classes for the square
        square.className = `w-8 h-8 rounded flex items-center justify-center text-xs font-semibold`;

        // Apply background color
        if (statusStyle.bg.startsWith("#")) {
          square.style.backgroundColor = statusStyle.bg;
        } else {
          square.classList.add(statusStyle.bg);
        }

        // Apply text color
        square.classList.add(statusStyle.text);

        const reportIdText = vr.value_report_id
          ? vr.value_report_id
          : "N/A (Value Report not created)";
        const clientNameText = vr.lender_client_name
          ? `${vr.lender_client_name} (LCID: ${vr.lender_client_id})`
          : "N/A";

        square.title = `ID: ${reportIdText}\nClient: ${clientNameText}\nLender: ${
          vr.lender_name
        }\nStatus: ${vr.report_status}\nCreated: ${new Date(
          vr.created_at
        ).toLocaleString()}`;

        // Make the square clickable to navigate to the event details page
        square.classList.add(
          "cursor-pointer",
          "hover:ring-2",
          "hover:ring-purple-500",
          "hover:ring-offset-1"
        );
        square.addEventListener("click", () => {
          // Ensure vr.event_id is available from the data fetcher
          // (it should be, as 'vr_creation_events.id AS event_id')
          if (vr.event_id) {
            window.location.href = `/vr_monitoring/events/${vr.event_id}`;
          } else {
            console.error("Event ID is missing, cannot navigate to details.");
            // Optionally, provide user feedback that details aren't available
          }
        });

        this.statusGridTarget.appendChild(square);
      });
    } catch (error) {
      console.error("Error during renderStatusGrid execution:", error);
      this.statusGridTarget.innerHTML =
        '<p class="text-red-500">Error rendering VR status grid. Check console for details.</p>';
    }
  }
}
